import { defaultFonts } from 'apps/cdp-web-demo/app/data/defaultFonts';
import { ConfigProps } from 'apps/cdp-web-demo/app/types';
import { Element, Root, Text } from 'hast';

export function generateCodeSnippet(props: ConfigProps) {
  const { theme: themeProp, config } = props;
  const theme = themeProp;
  if (theme['font-family-sans'] === defaultFonts.sans) {
    delete theme['font-family-sans'];
  }
  if (theme['font-family-mono'] === defaultFonts.mono) {
    delete theme['font-family-mono'];
  }
  if (theme['font-family-body'] === defaultFonts.body) {
    delete theme['font-family-body'];
  }
  if (theme['font-family-interactive'] === defaultFonts.interactive) {
    delete theme['font-family-interactive'];
  }
  return `
import { CDPReactProvider, AuthButton, type Config, type Theme } from "@coinbase/cdp-react";

const config: Config = {
  projectId: "your-project-id",${
    config.ethereum?.createOnLogin
      ? `
  ethereum: {
    createOnLogin: "${config.ethereum.createOnLogin}",
  },`
      : ''
  }${
    config.solana?.createOnLogin
      ? `
  solana: {
    createOnLogin: ${config.solana.createOnLogin},
  },`
      : ''
  }
  appName: "${config.appName ?? 'My App'}",
  appLogoUrl: "${config.appLogoUrl ?? ''}",
  authMethods: ${JSON.stringify(config.authMethods ?? ['email'])},
  showCoinbaseFooter: ${config.showCoinbaseFooter ?? true},
}

const theme: Partial<Theme> = ${JSON.stringify(theme, null, 2) ?? {}}

export default App = () => {
  return (
    <CDPReactProvider config={config} theme={theme}>
      <AuthButton />
    </CDPReactProvider>
  );
}`.trim();
}

/**
 * Wraps a type name in a span with the classes "token" and "type-name"
 * Used in custom post processing of the tree to highlight type names
 *
 * @param node - The node to wrap in a span
 * @returns A parent span element with the original node wrapped in a span with the classes "token" and "type-name" along with the leading and trailing whitespace if they exist
 */
function wrapTypeInToken(node: Text): Text | Element {
  const text = node.value;
  const match = text.match(/^(\s*)([A-Z][a-zA-Z0-9]*)(\s*)$/);

  if (!match) {
    // No type name found, return as-is
    return node;
  }

  const [, leadingSpace, typeName, trailingSpace] = match;

  return {
    type: 'element',
    tagName: 'span',
    properties: {
      className: ['token'],
    },
    children: [
      ...(leadingSpace ? [{ type: 'text', value: leadingSpace } satisfies Text] : []),
      {
        type: 'element',
        tagName: 'span',
        properties: {
          className: ['token', 'type-name'],
        },
        children: [{ type: 'text', value: typeName } satisfies Text],
      },
      ...(trailingSpace ? [{ type: 'text', value: trailingSpace } satisfies Text] : []),
    ],
  };
}

/**
 * Checks if the given value matches the regex
 * Used to determine if the node should be highlighted as a type name
 *
 * Matches "Theme", "Partial", or "Config" with optional leading and trailing whitespace
 *
 * @param value - The value to check if it matches the regex
 * @returns boolean - Whether the value matches the regex
 */
function isMatch(value: string) {
  return /^(\s*)(Theme|Partial|Config)(\s*)$/.test(value);
}

export function postProcessTree(tree: Root) {
  const children = tree.children.map((n) =>
    n.type === 'text' && isMatch(n.value) ? wrapTypeInToken(n) : n,
  );
  return {
    ...tree,
    children,
  };
}
